/**
 * Spell utility functions
 */
/**
 * Parse whether a spell description indicates a spell attack roll.
 * Open5e descriptions consistently use "ranged spell attack" and "melee spell attack".
 */
export function parseSpellAttackType(desc) {
    const lower = desc.toLowerCase();
    if (lower.includes('ranged spell attack'))
        return 'ranged';
    if (lower.includes('melee spell attack'))
        return 'melee';
    return null;
}
/**
 * Parse whether a spell description requires a saving throw.
 * Open5e descriptions consistently use "{Ability} saving throw" patterns.
 * Returns the required save ability or null if the spell doesn't require a save.
 */
export function parseSpellSaveType(desc) {
    const lower = desc.toLowerCase();
    const abilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
    for (const ability of abilities) {
        if (lower.includes(`${ability} saving throw`)) {
            return { ability };
        }
    }
    return null;
}
/**
 * Parse the first damage expression from a spell description.
 * Open5e descriptions consistently use "NdM type damage" patterns.
 * Returns null if no damage is found.
 */
export function parseSpellDamage(desc) {
    const match = desc.match(/(\d+)d(\d+)\s+(\w+)\s+damage/i);
    if (!match)
        return null;
    const dieCount = parseInt(match[1]);
    const dieSize = parseInt(match[2]);
    return {
        dice: `${dieCount}d${dieSize}`,
        damageType: match[3].toLowerCase(),
        dieCount,
        dieSize,
    };
}
/**
 * Parse healing dice from a spell description.
 * Open5e healing spells consistently use "regains hit points equal to NdM + your
 * spellcasting ability modifier" or "regains NdM hit points" patterns.
 */
export function parseSpellHealing(desc) {
    const lower = desc.toLowerCase();
    // Only match if the spell talks about regaining hit points
    if (!lower.includes('regains') && !lower.includes('hit points'))
        return null;
    // Don't match damage spells that happen to mention hit points
    if (lower.includes('damage'))
        return null;
    // Pattern with ability modifier: "NdM + your spellcasting ability modifier"
    const modMatch = desc.match(/(\d+)d(\d+)\s*\+\s*your spellcasting ability modifier/i);
    if (modMatch) {
        const dieCount = parseInt(modMatch[1]);
        const dieSize = parseInt(modMatch[2]);
        return { dice: `${dieCount}d${dieSize}`, dieCount, dieSize, addAbilityMod: true };
    }
    // Pattern without modifier: "regains NdM hit points" or "hit points equal to NdM"
    const plainMatch = desc.match(/(\d+)d(\d+)(?:\s+hit\s+points|\s+hp)/i);
    if (plainMatch) {
        const dieCount = parseInt(plainMatch[1]);
        const dieSize = parseInt(plainMatch[2]);
        return { dice: `${dieCount}d${dieSize}`, dieCount, dieSize, addAbilityMod: false };
    }
    return null;
}
/**
 * Scale cantrip damage based on total character level.
 * Standard 5e cantrip scaling: 1 die at 1st, 2 at 5th, 3 at 11th, 4 at 17th.
 */
export function scaleCantripDamage(baseDamage, characterLevel) {
    let multiplier = 1;
    if (characterLevel >= 17)
        multiplier = 4;
    else if (characterLevel >= 11)
        multiplier = 3;
    else if (characterLevel >= 5)
        multiplier = 2;
    const scaledCount = baseDamage.dieCount * multiplier;
    return {
        ...baseDamage,
        dice: `${scaledCount}d${baseDamage.dieSize}`,
        dieCount: scaledCount,
    };
}
/** Return true when the character has a feature whose name contains the query (case-insensitive). */
function hasFeature(features, query) {
    const q = query.toLowerCase();
    return features.some((f) => f.name.toLowerCase().includes(q));
}
/**
 * Resolve a spell's damage through 5e class features + invocations.
 *
 * Handles:
 *  - Eldritch Blast multi-beam scaling (1 beam at 1st, 2 at 5th, 3 at 11th, 4 at 17th)
 *  - Agonizing Blast invocation (+CHA mod to each Eldritch Blast beam)
 *  - Booming Blade / Green-Flame Blade ability-mod damage (leveled)
 *  - Standard cantrip die-count scaling for non-beam cantrips
 *
 * Other spells (levelled damage) return the base damage unchanged — those use
 * spell-slot scaling which needs the slot level at cast time (separate path).
 */
export function resolveSpellDamage(ctx) {
    const base = parseSpellDamage(ctx.description);
    if (!base)
        return null;
    const notes = [];
    const lower = ctx.spellName.toLowerCase();
    const isCantrip = /at higher levels\. when you reach/i.test(ctx.description) === false
        ? /when you reach (\d+)(?:th|st|nd|rd)/i.test(ctx.description) || /at higher levels/i.test(ctx.description)
        : true;
    // --- Eldritch Blast special case ---
    if (lower.includes('eldritch blast')) {
        // Beams: 1 at 1st, 2 at 5th, 3 at 11th, 4 at 17th (overrides die-count scaling)
        let beams = 1;
        if (ctx.characterLevel >= 17)
            beams = 4;
        else if (ctx.characterLevel >= 11)
            beams = 3;
        else if (ctx.characterLevel >= 5)
            beams = 2;
        let bonus = 0;
        if (hasFeature(ctx.features, 'agonizing blast')) {
            bonus = ctx.abilityMod;
            notes.push(`Agonizing Blast: +${ctx.abilityMod} per beam`);
        }
        if (beams > 1)
            notes.push(`${beams} beams — roll each attack + damage separately`);
        return {
            dicePerHit: `${base.dieCount}d${base.dieSize}`,
            dieCountPerHit: base.dieCount,
            dieSize: base.dieSize,
            bonusPerHit: bonus,
            hitCount: beams,
            damageType: base.damageType,
            notes,
        };
    }
    // --- Booming Blade / Green-Flame Blade: add CHA/WIS/INT mod to damage ---
    if (lower.includes('booming blade') || lower.includes('green-flame blade') || lower.includes('green flame blade')) {
        // These scale differently — weapon damage + extra dice at higher levels.
        // Simplified: pass through base damage + ability mod.
        return {
            dicePerHit: `${base.dieCount}d${base.dieSize}`,
            dieCountPerHit: base.dieCount,
            dieSize: base.dieSize,
            bonusPerHit: ctx.abilityMod,
            hitCount: 1,
            damageType: base.damageType,
            notes: [`+${ctx.abilityMod} from spellcasting ability`],
        };
    }
    // --- Standard cantrip scaling (non-beam cantrips: Fire Bolt, Sacred Flame, etc.) ---
    if (isCantrip) {
        const scaled = scaleCantripDamage(base, ctx.characterLevel);
        return {
            dicePerHit: scaled.dice,
            dieCountPerHit: scaled.dieCount,
            dieSize: scaled.dieSize,
            bonusPerHit: 0,
            hitCount: 1,
            damageType: scaled.damageType,
            notes: ctx.characterLevel >= 5 ? [`Cantrip scaling: ${scaled.dieCount} dice at level ${ctx.characterLevel}`] : [],
        };
    }
    // --- Leveled spells: return base unchanged (slot-level scaling is separate) ---
    return {
        dicePerHit: base.dice,
        dieCountPerHit: base.dieCount,
        dieSize: base.dieSize,
        bonusPerHit: 0,
        hitCount: 1,
        damageType: base.damageType,
        notes: [],
    };
}
/**
 * Compute the spell attack bonus for a character casting a spell.
 * = spellcasting ability modifier + proficiency bonus
 */
export function getSpellAttackBonus(abilityMod, proficiencyBonus) {
    return abilityMod + proficiencyBonus;
}
/**
 * Compute the spell save DC for a character casting a spell.
 * = 8 + spellcasting ability modifier + proficiency bonus
 */
export function getSpellSaveDc(abilityMod, proficiencyBonus) {
    return 8 + abilityMod + proficiencyBonus;
}
//# sourceMappingURL=spell-utils.js.map